import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const total = heroSlides.length;

  const go = (next: number) => setIndex((next + total) % total);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), 7000);
    return () => clearInterval(timer);
  }, [total]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
    touchStartX.current = null;
  };

  return (
    <section
      aria-label="焦点专题"
      className="relative w-full overflow-hidden rounded-lg bg-background-100 border border-background-200"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="relative h-[420px] sm:h-[480px] md:h-[560px]">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            aria-hidden={i !== index}
          >
            <div className="absolute inset-0 w-full h-full">
              <img
                src={slide.image}
                alt={`${slide.title} 焦点图`}
                title={`${slide.title} - 美国禁忌4`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/55 to-background-50/10" />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50/80 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 h-full flex items-end">
          <div className="w-full px-6 md:px-12 pb-8 md:pb-14">
            <div key={heroSlides[index].id} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/20 border border-accent-500/40 px-3 py-1 text-xs font-label tracking-widest text-accent-300 uppercase">
                <i className="ri-fire-line" />
                {heroSlides[index].badge}
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-5xl font-semibold tracking-wide text-foreground-950">
                {heroSlides[index].title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed max-w-xl">
                {heroSlides[index].subtitle}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={heroSlides[index].to}
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label tracking-wide text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg" />
                  {heroSlides[index].cta}
                </Link>
                <Link
                  to="/episodes"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-label tracking-wide text-foreground-800 hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部章节
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Manual controls */}
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur border border-background-300 text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/70 backdrop-blur border border-background-300 text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>

        <div className="absolute bottom-4 right-6 md:right-12 z-20 flex items-center gap-2">
          {heroSlides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-7 bg-accent-500" : "w-3 bg-foreground-600/50 hover:bg-foreground-500"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}